import { useEffect, useRef } from 'react';
import { captureException } from '@sentry/gatsby';
import { useLocation } from '../context/LocationContext';
import { useLocation as UseRouterLocation } from '@reach/router';
import { getSessionStorage, initDataLayer, normalizePhone, setSessionStorage } from '../components/utilities/functions';

const USER_DATA_KEY = 'route_change_user_data';

const getStoredUserData = () => getSessionStorage(USER_DATA_KEY);

/**
 * @typedef {Object} UserData
 * @property {string} [firstname] - User's first name
 * @property {string} [lastname] - User's last name
 * @property {string} [email] - User's email address
 * @property {string} [phone] - User's phone number
 * @property {string} [branchID] - Associated branch ID
 */

/**
 * @typedef {Object} Profile
 * @property {string} [firstName] - User's first name from profile
 * @property {string} [lastName] - User's last name from profile
 * @property {string} [email] - User's email from profile
 * @property {string} [mobilePhone] - User's phone from profile
 */

/**
 * @typedef {Object} RouteChangeConfig
 * @property {UserData|null} [userData] - Form/appointment data to track
 * @property {Profile|null} [profile] - User profile data as fallback
 * @property {string} [pageType] - Type of page being viewed (defaults to an empty string if null or undefined)
 * @property {string} [testFlag] - Test flag for data layer
 * @property {string|null} [branchID] - Template branch ID to override other branch ID sources
 * @property {boolean} [suppressDataLayer=false] - Flag to suppress data layer events
 * @property {string} [pageTitle] - Title of the current page (uses `document.title` if not provided)
 * @property {boolean} [isLogged] - Whether the user is logged in
 * @property {boolean} [ecommerce] - Whether the page is ecommerce
 */

/**
 * Hook to handle route changes and initialize the data layer with user information.
 * Persists user data in session storage and prioritizes data sources in the following order:
 * 1. Provided `userData`
 * 2. Session stored `userData`
 * 3. `profile` data
 * 4. Empty strings as fallback
 *
 * @param {RouteChangeConfig} config - Configuration object
 *
 * @example
 * // With form data
 * useRouteChange({
 *   userData: appointmentData,
 *   pageType: 'confirmation',
 *   testFlag: 'test',
 *   pageTitle: 'Confirmation Page'
 * });
 *
 * @example
 * // With profile data
 * useRouteChange({
 *   profile: userProfile,
 *   pageType: 'account',
 *   pageTitle: 'Account Page'
 * });
 *
 * @example
 * // Suppressing data layer events
 * useRouteChange({
 *   userData: appointmentData,
 *   suppressDataLayer: true,
 *   pageTitle: 'Form Page'
 * });
 */
export const useRouteChange = ({ userData = null, profile = null, pageType = '', testFlag, branchID: templateBranchId = null, suppressDataLayer = false, pageTitle, isLogged, ecommerce } = {}) => {
	const { selectedShowroom, setRouteChangeComplete, branchInitialized, setBranchInitialized } = useLocation();

	const location = UseRouterLocation();
	const lastRouteRef = useRef('');
	const hasInitializedRef = useRef(false);

	useEffect(() => {
		if (userData) {
			setSessionStorage(USER_DATA_KEY, userData);
		}
	}, [userData]);

	useEffect(() => {
		try {
			const storedUserData = getStoredUserData();
			const effectiveUserData = userData || storedUserData;

			const branchId = templateBranchId || effectiveUserData?.branchID || selectedShowroom?.branch_id;

			if (!branchId || branchInitialized) return;

			const currentBranchId = window.dataLayer?.find((event) => event.event === 'getBranchID')?.branchID;

			if (currentBranchId !== branchId) {
				initDataLayer('getBranchID', {
					branchID: branchId,
					...(testFlag && { test: testFlag }),
				});

				setBranchInitialized(true);
			}
		} catch (error) {
			captureException(error);
		}
	}, [selectedShowroom?.branch_id, userData, branchInitialized, testFlag, setBranchInitialized, templateBranchId]);

	useEffect(() => {
		if (!branchInitialized) {
			return;
		}

		const currentPath = window.location.href;

		if (!hasInitializedRef.current || lastRouteRef.current !== currentPath) {
			const storedUserData = getStoredUserData();
			const effectiveUserData = userData || storedUserData;

			const firstName = effectiveUserData?.firstname ?? profile?.firstName ?? '';
			const lastName = effectiveUserData?.lastname ?? profile?.lastName ?? '';
			const email = effectiveUserData?.email ?? profile?.email ?? '';
			const rawPhone = effectiveUserData?.phone ?? profile?.mobilePhone ?? '';
			const phone = normalizePhone(rawPhone);
			const effectivePageTitle = pageTitle ? `${pageTitle} | Pella` : document.title;

			if (!suppressDataLayer) {
				const internalReferrer = location?.state?.referrer;
				let previousUrl = '';

				if (internalReferrer) {
					previousUrl = `${process.env.GATSBY_SITE_URL}${internalReferrer}`;
				} else if (document.referrer && document.referrer !== currentPath) {
					previousUrl = document.referrer;
				}

				const dataLayerObject = {
					event: 'gatsby-route-change',
					page_type: pageType || '',
					first_name: firstName,
					last_name: lastName,
					email_address: email,
					phone_number: phone,
					pageUrl: currentPath,
					previousUrl: previousUrl,
					pageTitle: effectivePageTitle,
					...(testFlag && { test: testFlag }),
				};

				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push(dataLayerObject);
				setRouteChangeComplete(true);

				const isEcommerce = typeof ecommerce === 'boolean' ? ecommerce : false;
				if (typeof ecommerce === 'boolean' || typeof isLogged === 'boolean') {
					window.dataLayer.push({
						event: 'pageMetaData',
						user: {
							loggedIn: isLogged ? 'True' : 'False',
							isEcommerce: isEcommerce,
						},
					});
				}
			}

			lastRouteRef.current = currentPath;
			hasInitializedRef.current = true;
		}

		return () => {
			setRouteChangeComplete(false);
		};
	}, [branchInitialized, userData, profile, pageType, testFlag, suppressDataLayer, setRouteChangeComplete, pageTitle, location?.state?.referrer, isLogged, ecommerce]);
};
